import { Capacitor } from '@capacitor/core';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { Preferences } from '@capacitor/preferences';
import { Dialog } from '@capacitor/dialog';
import { getGlobalIntl } from '../../reduxConnectedIntlProvider';
import { tryTranslate } from '../locale';

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const server = PUBLIC_URL.replace('https://', '')
  .replace('http://', '')
  .split(':')[0];

const verifyIdentity = () => {
  const intl = getGlobalIntl();

  return NativeBiometric.verifyIdentity({
    reason: intl.formatMessage({ id: 'biometric-verify-title' }),
    title: intl.formatMessage({ id: 'biometric-verify-title' }),
    subtitle: intl.formatMessage({ id: 'biometric-verify-subtitle' }),
    maxAttempts: 5,
    useFallback: true
  });
};

export const enableBiometricAuth = async () => {
  const currentPlatform = Capacitor.getPlatform();
  if (currentPlatform !== 'web') {
    try {
      let isVerified;
      try {
        const verifyResult = await verifyIdentity();
        isVerified = true;
      } catch (error) {
        isVerified = false;
      }

      if (isVerified) {
        const storageResult = await Preferences.set({ key: 'biometricSet', value: JSON.stringify(true) });
        return true;
      }
    } catch (error) {
      return false;
    }
  }

  return false;
};

export const disableBiometricAuth = async () => {
  const currentPlatform = Capacitor.getPlatform();
  if (currentPlatform !== 'web') {
    try {
      await Preferences.remove({ key: 'biometricSet' });
      return true;
    } catch (error) {
      return false;
    }
  }

  return false;
};

export const isBiometricAvailable = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      const isAvailableResult = await NativeBiometric.isAvailable();
      return isAvailableResult.isAvailable;
    } catch (error) {
      // console.log('isBiometricAvailable error', error);
    }
  }

  return false;
};

export const isBiometricSet = async () => {
  const currentPlatform = Capacitor.getPlatform();
  if (currentPlatform === 'web') {
    return false;
  } else {
    const isAvailableResult = await NativeBiometric.isAvailable();
    const isAvailable = isAvailableResult.isAvailable;

    if (isAvailable) {
      let storageResult, credentials;

      try {
        storageResult = await Preferences.get({ key: 'biometricSet' });
      } catch (error) {
        storageResult = {};
      }

      try {
        credentials = await NativeBiometric.getCredentials({ server: server });
      } catch (error) {
        credentials = {};
      }

      return !!JSON.parse(storageResult.value) && !!credentials.username && !!credentials.password;
    }

    return false;
  }
};

export const askBiometricAuth = async (username, password) => {
  const currentPlatform = Capacitor.getPlatform();
  if (currentPlatform === 'web') {
    return false;
  } else {
    let result;

    const isAvailableResult = await NativeBiometric.isAvailable();
    const isAvailable = isAvailableResult.isAvailable;

    if (isAvailable) {
      let askedStorageResult;
      try {
        askedStorageResult = await Preferences.get({ key: 'askedEnableBiometric' });
      } catch (error) {
        askedStorageResult = {};
      }
      const asked = !!JSON.parse(askedStorageResult.value);

      let biometricSetResult;
      try {
        biometricSetResult = await Preferences.get({ key: 'biometricSet' });
      } catch (error) {
        biometricSetResult = {};
      }
      const biometricAlreadyEnabled = !!JSON.parse(biometricSetResult.value);

      if (!asked && !biometricAlreadyEnabled) {
        let confirmResult;
        try {
          const intl = getGlobalIntl();

          confirmResult = await Dialog.confirm({
            title: tryTranslate(intl, 'ask-biometric-title'),
            message: tryTranslate(intl, 'ask-biometric-content')
          });

          try {
            const setStorageResult = await Preferences.set({
              key: 'askedEnableBiometric',
              value: JSON.stringify(true)
            });
          } catch (error) {
            // console.log('storage error', error);
          }
        } catch (error) {
          confirmResult = { value: false };
        }

        if (!!confirmResult.value) {
          try {
            await setBiometricCredentials(username, password);
          } catch (error) {
            result = false;
          }
          const enabled = await enableBiometricAuth();
          result = enabled;
        }
      }
    }

    return result;
  }
};

export const setBiometricCredentials = async (username, password) => {
  await NativeBiometric.deleteCredentials({ server: server });

  return await NativeBiometric.setCredentials({
    username: username,
    password: password,
    server: server
  });
};

export const getBiometricCredentials = async () => {
  const currentPlatform = Capacitor.getPlatform();
  if (currentPlatform === 'web') {
    return undefined;
  } else {
    try {
      const credentials = await NativeBiometric.getCredentials({ server: server });
      return credentials;
    } catch (error) {
      return undefined;
    }
  }
};

export const getBiometricCredentialsWithVerification = async () => {
  const currentPlatform = Capacitor.getPlatform();
  if (currentPlatform === 'web') {
    return undefined;
  } else {
    try {
      // Check if biometrics are available and which type is supported
      const isAvailableResult = await NativeBiometric.isAvailable();
      const isAvailable = isAvailableResult.isAvailable;

      if (isAvailable) {
        // Get user's credentials
        const credentials = await NativeBiometric.getCredentials({ server: server });

        await verifyIdentity();

        return credentials;
      }

      return undefined;
    } catch (error) {
      return undefined;
    }
  }
};
